// Manual mode for the screen: screenshot + UI tree, accessibility actions on
// nodes (preferred), and coordinate taps as the fallback. Every action still
// goes through the phone's broker.

import { UiNodeFlag, type UiNode, type UiTreeSnapshot } from "@fistos/shared";
import type { ScreenshotInfo } from "@fistos/shared/api";
import {
  Bell,
  House,
  Image,
  Keyboard,
  LayoutGrid,
  ListTree,
  LoaderCircle,
  Lock,
  MousePointerClick,
  PanelTop,
  Play,
  ShieldAlert,
  Undo2,
} from "lucide-solid";
import { createMemo, createSignal, For, onCleanup, Show, type JSX } from "solid-js";
import { toast } from "somoto";
import { ToneBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch, SwitchControl, SwitchInput, SwitchLabel, SwitchThumb } from "@/components/ui/switch";
import { TextField, TextFieldInput } from "@/components/ui/text-field";
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip";
import { api, errorText } from "@/lib/api";
import { live, setUiTree } from "@/lib/live";
import { actions, GlobalActionType, parse, statusName, UiActionType } from "@/lib/proto";
import { OfflineNote, run, type PanelProps } from "./common";

const has = (n: UiNode, f: UiNodeFlag) => (n.flags & f) !== 0;

function label(n: UiNode): string {
  return n.text || n.contentDescription || n.hint || n.viewId.replace(/^.*:id\//, "") || "";
}

function shortClass(c: string) {
  return c.slice(c.lastIndexOf(".") + 1);
}

function IconAction(props: { label: string; onClick: () => void; disabled?: boolean; children: JSX.Element }) {
  return (
    <Tooltip>
      <TooltipTrigger
        as={Button<"button">}
        variant="outline"
        size="icon-sm"
        aria-label={props.label}
        disabled={props.disabled}
        onClick={props.onClick}
      >
        {props.children}
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>{props.label}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}

export function ScreenPanel(props: PanelProps) {
  const id = () => props.device.id;
  const [shot, setShot] = createSignal<ScreenshotInfo | null>(null);
  const [busy, setBusy] = createSignal<string | null>(null);
  const [overlay, setOverlay] = createSignal(true);
  const [tapMode, setTapMode] = createSignal(false);
  const [subscribed, setSubscribed] = createSignal(false);
  const [selected, setSelected] = createSignal<bigint | null>(null);
  const [filter, setFilter] = createSignal("");
  const [text, setText] = createSignal("");
  const [typed, setTyped] = createSignal("");
  const [pkg, setPkg] = createSignal("");

  const tree = createMemo<UiTreeSnapshot | null>(() => {
    const t = live.uiTree[id()];
    return t ? parse.tree(t) : null;
  });
  const node = () => tree()?.nodes.find((n) => n.id === selected());
  const visibleNodes = createMemo(() => {
    const q = filter().toLowerCase();
    return (tree()?.nodes ?? []).filter(
      (n) => !q || label(n).toLowerCase().includes(q) || n.className.toLowerCase().includes(q),
    );
  });

  const withBusy = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    try {
      await fn();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const capture = () =>
    withBusy("shot", async () => {
      const r = await api.observe(id(), { kind: "screenshot", quality: 80 });
      if (r.screenshot) setShot(r.screenshot);
      else if (r.result)
        throw new Error(
          `Screenshot ${statusName(parse.result(r.result)).toLowerCase()}: ${parse.result(r.result).message}`,
        );
    });
  const readTree = () =>
    withBusy("tree", async () => {
      const r = await api.observe(id(), { kind: "ui_tree" });
      if (r.uiTree) setUiTree(id(), r.uiTree);
      else if (r.result) throw new Error(parse.result(r.result).message || "UI tree unavailable");
    });
  const refresh = async () => {
    await readTree();
    await capture();
  };
  const toggleLive = async (on: boolean) => {
    try {
      await api.observe(
        id(),
        on ? { kind: "ui_tree", subscribe: true, minIntervalMs: 500 } : { kind: "ui_tree", unsubscribe: true },
      );
      setSubscribed(on);
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  onCleanup(() => {
    if (subscribed()) void api.observe(id(), { kind: "ui_tree", unsubscribe: true }).catch(() => {});
  });

  const act = async (what: string, action: Parameters<typeof run>[1]) => {
    await withBusy(what, async () => {
      if (await run(id(), action)) setTimeout(() => void refresh(), 700);
    });
  };
  const global = (a: GlobalActionType, what: string) => act(what, actions.global(a));

  // Bounds are in screen pixels; the screenshot may be scaled down.
  const scale = createMemo(() => {
    const s = shot();
    const nodes = tree()?.nodes ?? [];
    const maxRight = Math.max(0, ...nodes.map((n) => n.bounds?.right ?? 0));
    if (!s || !maxRight) return 1;
    return maxRight > s.width * 1.05 ? s.width / maxRight : 1;
  });

  let img!: HTMLImageElement;
  const onImageClick = (e: MouseEvent) => {
    if (!tapMode() || !shot()) return;
    const rect = img.getBoundingClientRect();
    const x = Math.round((((e.clientX - rect.left) / rect.width) * shot()!.width) / scale());
    const y = Math.round((((e.clientY - rect.top) / rect.height) * shot()!.height) / scale());
    void act("tap", actions.tap(x, y));
  };

  const boxes = createMemo(() =>
    (tree()?.nodes ?? []).filter(
      (n) =>
        n.bounds &&
        (has(n, UiNodeFlag.CLICKABLE) || has(n, UiNodeFlag.EDITABLE) || has(n, UiNodeFlag.SCROLLABLE) || label(n)),
    ),
  );

  return (
    <div class="grid gap-4">
      <Show when={!props.device.online}>
        <OfflineNote device={props.device} />
      </Show>
      <div class="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={refresh} disabled={!props.device.online || !!busy()}>
          <Show when={busy() === "shot" || busy() === "tree"} fallback={<Image />}>
            <LoaderCircle class="animate-spin" />
          </Show>
          Capture
        </Button>
        <Button size="sm" variant="outline" onClick={readTree} disabled={!props.device.online || !!busy()}>
          <ListTree /> Read UI
        </Button>
        <Switch
          class="ml-1 flex items-center gap-2"
          checked={subscribed()}
          onChange={toggleLive}
          disabled={!props.device.online}
        >
          <SwitchInput />
          <SwitchControl>
            <SwitchThumb />
          </SwitchControl>
          <SwitchLabel class="text-xs font-normal">Live UI</SwitchLabel>
        </Switch>
        <div class="ml-auto flex flex-wrap gap-1">
          <IconAction
            label="Back"
            disabled={!props.device.online}
            onClick={() => global(GlobalActionType.GLOBAL_ACTION_BACK, "back")}
          >
            <Undo2 />
          </IconAction>
          <IconAction
            label="Home"
            disabled={!props.device.online}
            onClick={() => global(GlobalActionType.GLOBAL_ACTION_HOME, "home")}
          >
            <House />
          </IconAction>
          <IconAction
            label="Recent apps"
            disabled={!props.device.online}
            onClick={() => global(GlobalActionType.GLOBAL_ACTION_RECENTS, "recents")}
          >
            <LayoutGrid />
          </IconAction>
          <IconAction
            label="Notifications"
            disabled={!props.device.online}
            onClick={() => global(GlobalActionType.GLOBAL_ACTION_NOTIFICATIONS, "notifications")}
          >
            <Bell />
          </IconAction>
          <IconAction
            label="Quick settings"
            disabled={!props.device.online}
            onClick={() => global(GlobalActionType.GLOBAL_ACTION_QUICK_SETTINGS, "qs")}
          >
            <PanelTop />
          </IconAction>
          <IconAction
            label="Lock screen"
            disabled={!props.device.online}
            onClick={() => global(GlobalActionType.GLOBAL_ACTION_LOCK_SCREEN, "lock")}
          >
            <Lock />
          </IconAction>
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <div class="grid content-start gap-2">
          <div class="bg-muted/40 relative overflow-hidden rounded-2xl border p-1.5">
            <Show
              when={shot()}
              fallback={
                <div class="text-muted-foreground flex aspect-[9/19] flex-col items-center justify-center gap-2 text-center text-sm">
                  <Image class="size-6" />
                  No screenshot yet
                </div>
              }
            >
              {(s) => (
                <div class="relative">
                  <img
                    ref={img}
                    src={`data:${s().mime};base64,${s().dataBase64}`}
                    alt="Phone screen"
                    class="block w-full rounded-xl"
                    classList={{ "cursor-crosshair": tapMode() }}
                    onClick={onImageClick}
                  />
                  <Show when={overlay() && !tapMode()}>
                    <div class="pointer-events-none absolute inset-0">
                      <For each={boxes()}>
                        {(n) => {
                          const b = n.bounds!;
                          const sx = () => (scale() * 100) / s().width;
                          const sy = () => (scale() * 100) / s().height;
                          return (
                            <button
                              class="pointer-events-auto absolute rounded-[3px] border transition-colors"
                              classList={{
                                "border-primary bg-primary/20 ring-primary/40 ring-2": selected() === n.id,
                                "border-sky-500/50 hover:bg-sky-500/15": selected() !== n.id,
                              }}
                              style={{
                                left: `${b.left * sx()}%`,
                                top: `${b.top * sy()}%`,
                                width: `${(b.right - b.left) * sx()}%`,
                                height: `${(b.bottom - b.top) * sy()}%`,
                              }}
                              title={label(n) || shortClass(n.className)}
                              onClick={() => setSelected(n.id)}
                            />
                          );
                        }}
                      </For>
                    </div>
                  </Show>
                </div>
              )}
            </Show>
          </div>
          <Show when={shot()?.containsSecureContent}>
            <ToneBadge tone="warn">
              <ShieldAlert /> Contains a secure window
            </ToneBadge>
          </Show>
          <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Switch class="flex items-center gap-2" checked={overlay()} onChange={setOverlay}>
              <SwitchInput />
              <SwitchControl>
                <SwitchThumb />
              </SwitchControl>
              <SwitchLabel class="text-xs font-normal">Show elements</SwitchLabel>
            </Switch>
            <Switch class="flex items-center gap-2" checked={tapMode()} onChange={setTapMode}>
              <SwitchInput />
              <SwitchControl>
                <SwitchThumb />
              </SwitchControl>
              <SwitchLabel class="text-xs font-normal">Tap on screenshot (fallback)</SwitchLabel>
            </Switch>
          </div>
        </div>

        <div class="grid content-start gap-4">
          <Card class="gap-3 py-4">
            <CardHeader class="px-4">
              <CardTitle class="flex items-center justify-between text-sm">
                <span>
                  Elements
                  <Show when={tree()}>
                    <span class="text-muted-foreground font-normal"> · {tree()!.packageName}</span>
                  </Show>
                </span>
                <Show when={tree()?.truncated}>
                  <ToneBadge tone="warn">truncated</ToneBadge>
                </Show>
              </CardTitle>
            </CardHeader>
            <CardContent class="grid gap-3 px-4">
              <TextField value={filter()} onChange={setFilter}>
                <TextFieldInput placeholder="Filter by text or class" class="h-8" />
              </TextField>
              <div class="max-h-80 overflow-y-auto rounded-md border text-[13px]">
                <Show
                  when={visibleNodes().length}
                  fallback={
                    <div class="text-muted-foreground p-4 text-center text-sm">Read the UI to list elements.</div>
                  }
                >
                  <For each={visibleNodes()}>
                    {(n) => (
                      <button
                        class="hover:bg-accent flex w-full items-center gap-2 border-b px-2 py-1 text-left last:border-b-0"
                        classList={{ "bg-accent": selected() === n.id }}
                        style={{ "padding-left": `${0.5 + Math.min(n.depth, 12) * 0.6}rem` }}
                        onClick={() => setSelected(n.id)}
                      >
                        <span class="text-muted-foreground shrink-0 font-mono text-[11px]">
                          {shortClass(n.className)}
                        </span>
                        <span class="min-w-0 flex-1 truncate">{label(n)}</span>
                        <Show when={has(n, UiNodeFlag.CLICKABLE)}>
                          <MousePointerClick class="text-muted-foreground size-3.5 shrink-0" />
                        </Show>
                        <Show when={has(n, UiNodeFlag.EDITABLE)}>
                          <Keyboard class="text-muted-foreground size-3.5 shrink-0" />
                        </Show>
                      </button>
                    )}
                  </For>
                </Show>
              </div>
              <Show when={node()}>
                {(n) => (
                  <div class="bg-muted/40 grid gap-2 rounded-lg border p-3">
                    <div class="text-sm">
                      <span class="font-medium">{label(n()) || "(no label)"}</span>
                      <span class="text-muted-foreground">
                        {" "}
                        · {shortClass(n().className)} #{String(n().id)}
                      </span>
                    </div>
                    <div class="flex flex-wrap gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act("click", actions.ui(n().id, UiActionType.UI_ACTION_CLICK))}
                      >
                        Click
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act("long", actions.ui(n().id, UiActionType.UI_ACTION_LONG_CLICK))}
                      >
                        Long press
                      </Button>
                      <Show when={has(n(), UiNodeFlag.SCROLLABLE)}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => act("fwd", actions.ui(n().id, UiActionType.UI_ACTION_SCROLL_FORWARD))}
                        >
                          Scroll down
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => act("back", actions.ui(n().id, UiActionType.UI_ACTION_SCROLL_BACKWARD))}
                        >
                          Scroll up
                        </Button>
                      </Show>
                    </div>
                    <Show when={has(n(), UiNodeFlag.EDITABLE)}>
                      <form
                        class="flex gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          void act("set", actions.ui(n().id, UiActionType.UI_ACTION_SET_TEXT, text()));
                        }}
                      >
                        <TextField class="flex-1" value={text()} onChange={setText}>
                          <TextFieldInput placeholder="Set text" class="h-8" />
                        </TextField>
                        <Button size="sm" type="submit">
                          Set
                        </Button>
                      </form>
                    </Show>
                  </div>
                )}
              </Show>
            </CardContent>
          </Card>

          <Card class="gap-3 py-4">
            <CardContent class="grid gap-3 px-4">
              <form
                class="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (pkg().trim()) void act("launch", actions.launch(pkg().trim()));
                }}
              >
                <TextField class="flex-1" value={pkg()} onChange={setPkg}>
                  <TextFieldInput placeholder="Open app by package, e.g. com.android.settings" class="h-8" />
                </TextField>
                <Button size="sm" variant="outline" type="submit" disabled={!props.device.online}>
                  <Play /> Open
                </Button>
              </form>
              <form
                class="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (typed()) void act("type", actions.typeText(typed())).then(() => setTyped(""));
                }}
              >
                <TextField class="flex-1" value={typed()} onChange={setTyped}>
                  <TextFieldInput placeholder="Type into the focused field" class="h-8" />
                </TextField>
                <Button size="sm" variant="outline" type="submit" disabled={!props.device.online}>
                  <Keyboard /> Type
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
